import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import {
  Play, Plus, RefreshCcw, Search, Cpu, Zap, Activity, History,
  CheckCircle2, AlertTriangle, Clock, X, ArrowRight, ArrowLeft,
  MapPin as MapPinIcon, Filter, Sparkles, Upload, Coins, ShieldCheck,
  Layers, ChevronRight, FileText, Boxes,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Topbar } from "@/components/dashboard/Topbar";
import { GlassPanel } from "@/components/dashboard/GlassPanel";
import { cn } from "@/lib/utils";
import { RouteBadge } from "@/components/genrl/RouteBadge";
import { computeReadiness } from "@/components/genrl/CreditReadiness";
import { HostedProviderJobsPage } from "@/components/jobs/HostedProviderJobsPage";
import { buildFinancialBid, buildQuote, MICRO, ROUTE_LABEL, type RouteType } from "@/lib/genrl";
import {
  HARDWARE_COST_BANDS,
  assignHardwareCostBands,
  hardwareCostBandLabel,
} from "@/lib/hardware-cost-band";
import { APIError, apiRequest, financeUserID, readUISettings } from "@/lib/api";
import { isWalletCloudMode } from "@/lib/runtime-mode";
import { toast } from "sonner";

export const Route = createFileRoute("/run-job")({
  head: () => ({ meta: [{ title: "Run Workloads · GENRL" }] }),
  component: RunJobPage,
});

type JobState = "running" | "queued" | "completed" | "failed";
type Job = {
  id: string;
  state: JobState;
  client: string;
  gpu: string;
  region: string;
  pricePerHr: number;
  spent: number;
  updated: string;
  updatedAtMs: number;
  history: boolean;
};
type LiveBid = {
  source: string;
  gpu: string;
  gpuType?: string;
  flavorName?: string;
  stream: string;
  location: string;
  count: number;
  capacity: number;
  price: number;
  reference: boolean;
  optionId?: string;
  spokeId?: string;
  sourceSlug?: string;
  providerSlug?: string;
  providerOwnerUserID?: string;
  routeID?: string;
  routeType?: RouteType;
  offerToken?: string;
  providerUnitMicros?: number;
  genrlFeeMicros?: number;
  buyerUnitMicros?: number;
};

type BackendMarketOffer = {
  offer_token?: string;
  option_id?: string;
  spoke_id?: string;
  source_slug?: string;
  provider_slug?: string;
  provider_owner_user_id?: string;
  route_id?: string;
  source?: string;
  provider_name?: string;
  route_type?: RouteType;
  gpu?: string;
  flavor_name?: string;
  region_name?: string;
  stock_available?: number;
  price_per_hour?: number;
  provider_unit_price_micros?: number;
  genrl_unit_fee_micros?: number;
  buyer_unit_price_micros?: number;
};

type BackendJobRecord = {
  client_id?: string;
  request?: {
    job_id?: string;
    client_id?: string;
    selected_provider?: string;
    selected_gpu_type?: string;
    selected_gpu_count?: number;
    selected_region?: string;
    selected_spoke_id?: string;
    selected_provider_owner_user_id?: string;
  };
  state?: string;
  selected_spoke_id?: string;
  selected_provider_owner_user_id?: string;
  selected_provider?: string;
  selected_gpu_type?: string;
  selected_gpu_count?: number;
  selected_region?: string;
  selected_price_gpu_hr?: number;
  provisioned_provider?: string;
  provisioned_gpu_type?: string;
  provisioned_gpu_count?: number;
  provisioned_region?: string;
  provisioned_price_gpu_hr?: number;
  billed_total_value?: number;
  updated_at?: string;
};

type BackendJobDetail = {
  request?: {
    job_id?: string;
    client_id?: string;
    prompt?: string;
    selected_provider?: string;
    selected_region?: string;
    selected_gpu_type?: string;
    selected_gpu_count?: number;
    model_weights?: string;
  };
  state?: string;
  created_at?: string;
  updated_at?: string;
  progress_stage?: string;
  progress_message?: string;
  progress_detail?: string;
  failure_code?: string;
  failure_detail?: string;
  vm_ready?: boolean;
  vm_ready_at?: string;
  vm_terminated_at?: string;
  selected_provider?: string;
  selected_region?: string;
  selected_gpu_type?: string;
  selected_gpu_count?: number;
  selected_price_gpu_hr?: number;
  selected_currency?: string;
  provisioned_provider?: string;
  provisioned_region?: string;
  provisioned_gpu_type?: string;
  provisioned_gpu_count?: number;
  provisioned_price_gpu_hr?: number;
  provisioned_currency?: string;
  billed_total_value?: number;
  billed_currency?: string;
  billed_at?: string;
  output_plaintext?: string;
};

type BackendCreditsBalance = {
  available_credit_micros?: number;
};

type BackendProviderBalance = {
  source?: string;
  provider_slug?: string;
  credit?: number;
  threshold?: number;
};

type BackendProviderBalancesPayload = {
  providers?: BackendProviderBalance[];
};

type BackendDaemonAccountPayload = {
  configured?: boolean;
  user?: {
    id?: string;
  };
};

type AvailablePeer = {
  node_id?: string;
  api_base_url?: string;
  connected?: boolean;
};

type AvailablePeersPayload = {
  peers?: AvailablePeer[];
};

type IncomingPeerJob = {
  baseURL: string;
  row: BackendJobRecord;
};

type RuntimeHealthPayload = {
  hub?: boolean;
  spoke?: boolean;
  hub_id?: string;
};

function relTime(value?: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const mins = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

function mapJobState(raw: string): JobState {
  const state = String(raw || "").toUpperCase();
  if (state === "RUNNING") return "running";
  if (state === "COMPLETED") return "completed";
  if (state === "FAILED") return "failed";
  return "queued";
}

function fmtDateTime(value?: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function isTerminalState(raw?: string) {
  const state = String(raw || "").toUpperCase();
  return state === "COMPLETED" || state === "FAILED";
}

function mapBackendJob(row: BackendJobRecord): Job {
  const state = mapJobState(String(row.state || ""));
  const gpuType = String(row.provisioned_gpu_type || row.selected_gpu_type || row.request?.selected_gpu_type || "").trim();
  const gpuCount = Number(row.provisioned_gpu_count || row.selected_gpu_count || row.request?.selected_gpu_count || 0);
  const gpu = gpuType && gpuCount > 0 ? `${gpuType}×${gpuCount}` : "—";
  const price = Number(row.provisioned_price_gpu_hr || row.selected_price_gpu_hr || 0);
  const spent = Number(row.billed_total_value || 0);
  const provider = String(row.provisioned_provider || row.selected_provider || row.request?.selected_provider || "").trim() || "—";
  const region = String(row.provisioned_region || row.selected_region || row.request?.selected_region || "").trim() || "—";
  const id = String(row.request?.job_id || "").trim();
  const updatedAtMs = (() => {
    const raw = String(row.updated_at || "").trim();
    if (!raw) return 0;
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  })();
  return {
    id,
    state,
    client: provider,
    gpu,
    region,
    pricePerHr: price,
    spent,
    updated: relTime(row.updated_at),
    updatedAtMs,
    history: state === "completed" || state === "failed",
  };
}

function mapJobDetailToBackendRow(jobID: string, detail: BackendJobDetail): BackendJobRecord {
  return {
    request: {
      job_id: String(detail.request?.job_id || jobID || "").trim(),
      client_id: detail.request?.client_id,
      selected_provider: detail.request?.selected_provider,
      selected_gpu_type: detail.request?.selected_gpu_type,
      selected_gpu_count: detail.request?.selected_gpu_count,
      selected_region: detail.request?.selected_region,
    },
    state: detail.state,
    selected_provider: detail.selected_provider,
    selected_gpu_type: detail.selected_gpu_type,
    selected_gpu_count: detail.selected_gpu_count,
    selected_region: detail.selected_region,
    selected_price_gpu_hr: detail.selected_price_gpu_hr,
    provisioned_provider: detail.provisioned_provider,
    provisioned_gpu_type: detail.provisioned_gpu_type,
    provisioned_gpu_count: detail.provisioned_gpu_count,
    provisioned_region: detail.provisioned_region,
    provisioned_price_gpu_hr: detail.provisioned_price_gpu_hr,
    billed_total_value: detail.billed_total_value,
    updated_at: detail.updated_at,
  };
}

function mapBackendOffer(row: BackendMarketOffer): LiveBid {
  const source = String(row.provider_name || row.source || "").trim() || "—";
  const gpuType = String(row.gpu || "").trim();
  const flavorName = String(row.flavor_name || "").trim();
  const gpu = String(gpuType || flavorName || "").trim() || "—";
  const location = String(row.region_name || "").trim() || "—";
  const price = Number(row.price_per_hour || 0);
  const capacity = Math.max(0, Number(row.stock_available || 0));
  return {
    source,
    gpu,
    gpuType: gpuType || undefined,
    flavorName: flavorName || undefined,
    stream: String(row.source_slug || row.source || "").trim() || "—",
    location,
    count: 1,
    capacity,
    price,
    reference: true,
    optionId: row.option_id,
    spokeId: row.spoke_id,
    sourceSlug: row.source_slug,
    providerSlug: row.provider_slug,
    providerOwnerUserID: row.provider_owner_user_id,
    routeID: row.route_id,
    routeType: row.route_type,
    offerToken: row.offer_token,
    providerUnitMicros: Number(row.provider_unit_price_micros || 0),
    genrlFeeMicros: Number(row.genrl_unit_fee_micros || 0),
    buyerUnitMicros: Number(row.buyer_unit_price_micros || 0),
  };
}

function sameLiveBidIdentity(left: LiveBid, right: LiveBid) {
  const leftOptionID = String(left.optionId || "").trim();
  const rightOptionID = String(right.optionId || "").trim();
  if (leftOptionID && rightOptionID) {
    return leftOptionID === rightOptionID
      && normalizeScope(String(left.sourceSlug || "")) === normalizeScope(String(right.sourceSlug || ""))
      && normalizeScope(String(left.routeID || "")) === normalizeScope(String(right.routeID || ""))
      && normalizeScope(String(left.spokeId || "")) === normalizeScope(String(right.spokeId || ""));
  }
  return normalizeScope(String(left.sourceSlug || "")) === normalizeScope(String(right.sourceSlug || ""))
    && normalizeScope(String(left.providerSlug || "")) === normalizeScope(String(right.providerSlug || ""))
    && normalizeScope(String(left.providerOwnerUserID || "")) === normalizeScope(String(right.providerOwnerUserID || ""))
    && normalizeScope(String(left.routeID || "")) === normalizeScope(String(right.routeID || ""))
    && normalizeScope(String(left.spokeId || "")) === normalizeScope(String(right.spokeId || ""))
    && normalizeScope(String(left.gpuType || left.gpu || "")) === normalizeScope(String(right.gpuType || right.gpu || ""))
    && normalizeScope(String(left.flavorName || "")) === normalizeScope(String(right.flavorName || ""))
    && normalizeScope(String(left.location || "")) === normalizeScope(String(right.location || ""))
    && Number(left.count || 0) === Number(right.count || 0)
    && Number(left.price || 0) === Number(right.price || 0);
}

function normalizeScope(raw: string) {
  return String(raw || "").trim().toLowerCase();
}

function normalizeBaseURL(raw: string) {
  return String(raw || "").trim().replace(/\/+$/, "");
}

async function fetchJSONWithTimeout<T>(url: string, timeoutMs = 8000): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json() as T;
  } finally {
    window.clearTimeout(timer);
  }
}

function isJobTargetingLocalNode(job: BackendJobRecord, localHubID: string, localUserID: string) {
  const targetSpoke = normalizeScope(String(job.selected_spoke_id || job.request?.selected_spoke_id || ""));
  const targetOwner = normalizeScope(String(job.selected_provider_owner_user_id || job.request?.selected_provider_owner_user_id || ""));
  const hub = normalizeScope(localHubID);
  const user = normalizeScope(localUserID);
  return (hub.length > 0 && targetSpoke === hub) || (user.length > 0 && targetOwner === user);
}

async function loadIncomingPeerJobs(localHubID: string, localUserID: string): Promise<IncomingPeerJob[]> {
  if (!localHubID && !localUserID) return [];
  let peersPayload: AvailablePeersPayload;
  try {
    peersPayload = await apiRequest<AvailablePeersPayload>("/api/v1/network/peers/available", { method: "GET", timeoutMs: 12000 });
  } catch {
    return [];
  }
  const peers = Array.isArray(peersPayload.peers) ? peersPayload.peers : [];
  const connectedBases = peers
    .filter((p) => p.connected !== false)
    .map((p) => normalizeBaseURL(String(p.api_base_url || "")))
    .filter((base) => base.length > 0);
  if (connectedBases.length === 0) return [];

  const responses = await Promise.all(
    connectedBases.map(async (base) => {
      try {
        const rows = await fetchJSONWithTimeout<BackendJobRecord[]>(`${base}/api/v1/jobs`, 9000);
        return { baseURL: base, rows: Array.isArray(rows) ? rows : [] };
      } catch {
        return { baseURL: base, rows: [] };
      }
    }),
  );

  const merged: IncomingPeerJob[] = [];
  for (const { baseURL, rows } of responses) {
    rows.forEach((row) => {
      if (isJobTargetingLocalNode(row, localHubID, localUserID)) merged.push({ baseURL, row });
    });
  }
  return merged;
}

function slugToken(raw: string) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function toFinancialBid(bid: LiveBid, i: number) {
  return buildFinancialBid(
    {
      source: bid.source,
      gpu: bid.gpu,
      location: bid.location,
      price: bid.price,
      capacity: bid.capacity,
      reference: bid.reference,
      routeType: bid.routeType,
      sourceSlug: bid.sourceSlug,
      providerSlug: bid.providerSlug,
      routeID: bid.routeID,
      optionID: bid.optionId,
      spokeID: bid.spokeId,
      providerUnitMicros: bid.providerUnitMicros,
      genrlFeeMicros: bid.genrlFeeMicros,
      buyerUnitMicros: bid.buyerUnitMicros,
    },
    i,
  );
}

type ComputedFinancialBid = ReturnType<typeof buildFinancialBid>;

/* ---------------- helpers ---------------- */

const statePill = (s: JobState) => {
  switch (s) {
    case "running":   return { label: "Running",   cls: "border-[var(--success)]/30 bg-[var(--success)]/10 text-[var(--success)]", dot: true };
    case "queued":    return { label: "Queued",    cls: "border-[var(--warning)]/30 bg-[var(--warning)]/10 text-[var(--warning)]", dot: false };
    case "completed": return { label: "Completed", cls: "border-primary/30 bg-primary/10 text-primary",                            dot: false };
    case "failed":    return { label: "Failed",    cls: "border-destructive/30 bg-destructive/10 text-destructive",                dot: false };
  }
};

const fmtUsd = (n: number) => `$${n.toFixed(2)}`;

const JOB_LIFECYCLE_STEPS = [
  { key: "provision", label: "Provision", hint: "Create VM + attestation" },
  { key: "weights", label: "Weights", hint: "Stage model payloads" },
  { key: "transfer", label: "Transfer", hint: "Key handoff + runtime prep" },
  { key: "run", label: "Run", hint: "Inference in progress" },
  { key: "stream", label: "Finalize", hint: "Upload output + settle billing" },
] as const;

type JobLifecycleKey = typeof JOB_LIFECYCLE_STEPS[number]["key"];
type JobLifecycleState = "done" | "active" | "pending" | "failed";

function normalizeLifecycleStage(raw?: string): JobLifecycleKey | "" {
  const stage = String(raw || "").trim().toLowerCase();
  if (!stage) return "";
  if (stage.startsWith("prov")) return "provision";
  if (stage.includes("weight")) return "weights";
  if (stage.includes("transfer") || stage.includes("handoff") || stage.includes("deliver")) return "transfer";
  if (stage.startsWith("run") || stage.includes("infer")) return "run";
  if (stage.includes("stream") || stage.includes("output") || stage.includes("final")) return "stream";
  return "";
}

function lifecycleActiveIndex(rawState: string, rawStage?: string): number {
  const state = String(rawState || "").trim().toUpperCase();
  const normalizedStage = normalizeLifecycleStage(rawStage);
  const byStage = normalizedStage
    ? JOB_LIFECYCLE_STEPS.findIndex((s) => s.key === normalizedStage)
    : -1;

  if (state === "COMPLETED") return JOB_LIFECYCLE_STEPS.length - 1;
  if (state === "RUNNING" && byStage < 0) return 3;
  if (state === "FAILED" && byStage < 0) return 0;
  if (byStage >= 0) return byStage;
  return 0;
}

function lifecycleStates(rawState: string, rawStage?: string): JobLifecycleState[] {
  const state = String(rawState || "").trim().toUpperCase();
  const active = lifecycleActiveIndex(rawState, rawStage);
  return JOB_LIFECYCLE_STEPS.map((_, idx) => {
    if (state === "COMPLETED") return "done";
    if (state === "FAILED") {
      if (idx < active) return "done";
      if (idx === active) return "failed";
      return "pending";
    }
    if (idx < active) return "done";
    if (idx === active) return "active";
    return "pending";
  });
}

type TabId = "running" | "history";

const TONE: Record<string, string> = {
  violet: "text-[oklch(0.70_0.18_270)]",
  mint:   "text-[oklch(0.80_0.18_145)]",
  cyan:   "text-[oklch(0.78_0.16_200)]",
  amber:  "text-[oklch(0.82_0.17_80)]",
};

function StatChip({
  label, value, hint, icon: Icon, tone,
}: { label: string; value: string | number; hint: string; icon: LucideIcon; tone: keyof typeof TONE }) {
  return (
    <div className="glass flex items-center gap-3 rounded-xl px-4 py-3">
      <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg border hairline bg-soft", TONE[tone])}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold tracking-tight text-foreground">{value}</span>
          <span className="truncate text-[11px] text-muted-foreground">{hint}</span>
        </div>
      </div>
    </div>
  );
}

/* ---------------- page ---------------- */

export function RunJobPage() {
  if (isWalletCloudMode()) {
    return <HostedProviderJobsPage />;
  }

  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobOwnerBaseByID, setJobOwnerBaseByID] = useState<Record<string, string>>({});
  const [bids, setBids] = useState<LiveBid[]>([]);
  const [availableCreditMicros, setAvailableCreditMicros] = useState(0);
  const [providerBalanceByKey, setProviderBalanceByKey] = useState<Record<string, { creditMicros: number; thresholdMicros: number }>>({});
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<TabId>("running");
  const [query, setQuery] = useState("");
  const [wizard, setWizard] = useState(false);
  const [selectedJobID, setSelectedJobID] = useState<string | null>(null);
  const [jobDetail, setJobDetail] = useState<BackendJobDetail | null>(null);
  const [jobDetailBusy, setJobDetailBusy] = useState(false);
  const [jobDetailError, setJobDetailError] = useState("");

  async function refresh() {
    setBusy(true);
    try {
      const userID = financeUserID("");
      const creditsPayloadPromise = apiRequest<BackendCreditsBalance>("/api/wallet/balance", { method: "GET", timeoutMs: 15000 })
        .catch(() => {
          const creditsPath = userID
            ? `/api/v1/credits/balance?user_id=${encodeURIComponent(userID)}`
            : "/api/v1/credits/balance";
          return apiRequest<BackendCreditsBalance>(creditsPath, { method: "GET", timeoutMs: 15000 }).catch(() => ({} as BackendCreditsBalance));
        });
      const [jobsPayload, offersPayload, creditsPayload, balancesPayload, runtimeHealth, daemonAccount] = await Promise.all([
        apiRequest<BackendJobRecord[]>("/api/v1/jobs", { method: "GET", timeoutMs: 20000 }),
        apiRequest<{ offers?: BackendMarketOffer[] }>("/api/v1/market/offers", { method: "GET", timeoutMs: 20000 }),
        creditsPayloadPromise,
        apiRequest<BackendProviderBalancesPayload>("/api/v1/providers/balances", { method: "GET", timeoutMs: 18000 }).catch(() => ({ providers: [] })),
        apiRequest<RuntimeHealthPayload>("/api/v1/runtime/health", { method: "GET", timeoutMs: 12000 }).catch(() => ({} as RuntimeHealthPayload)),
        apiRequest<BackendDaemonAccountPayload>("/api/v1/auth/daemon-account", { method: "GET", timeoutMs: 12000 }).catch(() => ({} as BackendDaemonAccountPayload)),
      ]);

      const localHubID = String(runtimeHealth.hub_id || "").trim();
      const daemonUserID = String(daemonAccount.user?.id || "").trim();
      const peerIncomingJobs = await loadIncomingPeerJobs(localHubID, daemonUserID);

      const localJobs = Array.isArray(jobsPayload) ? jobsPayload : [];
      const localBaseURL = normalizeBaseURL(window.location.origin);
      const jobRowsByID = new Map<string, { row: BackendJobRecord; baseURL: string }>();
      localJobs.forEach((row) => {
        const id = String(row.request?.job_id || "").trim();
        if (!id) return;
        jobRowsByID.set(id, { row, baseURL: localBaseURL });
      });
      peerIncomingJobs.forEach(({ row, baseURL }) => {
        const id = String(row.request?.job_id || "").trim();
        if (!id) return;
        jobRowsByID.set(id, { row, baseURL: normalizeBaseURL(baseURL) });
      });

      const nextJobs = Array.from(jobRowsByID.values()).map(({ row }) => mapBackendJob(row)).filter((row) => row.id.length > 0);
      setJobs(nextJobs.length ? nextJobs : []);
      const nextJobOwners: Record<string, string> = {};
      jobRowsByID.forEach((value, id) => {
        nextJobOwners[id] = value.baseURL || localBaseURL;
      });
      setJobOwnerBaseByID(nextJobOwners);

      const offers = Array.isArray(offersPayload.offers) ? offersPayload.offers : [];
      const nextBids = offers.map(mapBackendOffer).filter((b) => b.capacity > 0 && b.price > 0);
      setBids(nextBids);

      setAvailableCreditMicros(Math.max(0, Number(creditsPayload.available_credit_micros || 0)));
      const nextBalanceByKey: Record<string, { creditMicros: number; thresholdMicros: number }> = {};
      const balanceRows = Array.isArray(balancesPayload.providers) ? balancesPayload.providers : [];
      balanceRows.forEach((row) => {
        const source = slugToken(String(row.source || ""));
        const provider = slugToken(String(row.provider_slug || ""));
        const creditMicros = Math.round(Number(row.credit || 0) * MICRO);
        const thresholdMicros = Math.round(Number(row.threshold || 0) * MICRO);
        if (source) nextBalanceByKey[`source:${source}`] = { creditMicros, thresholdMicros };
        if (provider) nextBalanceByKey[`provider:${provider}`] = { creditMicros, thresholdMicros };
      });
      setProviderBalanceByKey(nextBalanceByKey);
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to refresh jobs and bids");
      else toast.error("Failed to refresh jobs and bids");
      setJobs([]);
      setJobOwnerBaseByID({});
      setBids([]);
      setAvailableCreditMicros(0);
      setProviderBalanceByKey({});
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function ownerBaseForJob(jobID: string) {
    const mapped = normalizeBaseURL(jobOwnerBaseByID[jobID] || "");
    if (mapped) return mapped;
    return normalizeBaseURL(window.location.origin);
  }

  function ownerSettings(jobID: string) {
    const base = ownerBaseForJob(jobID);
    const current = readUISettings();
    return { ...current, hub_base: base };
  }

  async function loadJobDetail(jobID: string, silent = false, ownerBaseURL?: string) {
    if (!silent) setJobDetailBusy(true);
    try {
      const settingsOverride = ownerBaseURL
        ? { ...readUISettings(), hub_base: normalizeBaseURL(ownerBaseURL) }
        : ownerSettings(jobID);
      const payload = await apiRequest<BackendJobDetail>(`/api/v1/jobs/${encodeURIComponent(jobID)}`, {
        method: "GET",
        timeoutMs: 20000,
      }, settingsOverride);
      const detail = payload || null;
      setJobDetail(detail);
      if (detail) {
        const mapped = mapBackendJob(mapJobDetailToBackendRow(jobID, detail));
        if (mapped.id) {
          setJobs((current) => {
            let replaced = false;
            const next = current.map((job) => {
              if (job.id !== mapped.id) return job;
              replaced = true;
              return mapped;
            });
            return replaced ? next : [mapped, ...next];
          });
        }
      }
      setJobDetailError("");
    } catch (err) {
      const msg = err instanceof APIError ? (err.detail || "Failed to load job status") : "Failed to load job status";
      setJobDetailError(msg);
      toast.error(msg);
    } finally {
      if (!silent) setJobDetailBusy(false);
    }
  }

  async function openJobStatus(jobID: string) {
    setSelectedJobID(jobID);
    setJobDetail(null);
    setJobDetailError("");
    const ownerBaseURL = ownerBaseForJob(jobID);
    await loadJobDetail(jobID, false, ownerBaseURL);
  }

  async function cancelSelectedJob(jobID: string, ownerBaseURL?: string) {
    try {
      const settingsOverride = ownerBaseURL
        ? { ...readUISettings(), hub_base: normalizeBaseURL(ownerBaseURL) }
        : ownerSettings(jobID);
      await apiRequest(`/api/v1/jobs/${encodeURIComponent(jobID)}/cancel`, {
        method: "POST",
        body: {
          cancelled_by: "ui-operator",
          reason: "Cancelled from Run Workloads",
        },
        timeoutMs: 20000,
      }, settingsOverride);
      toast.success("Job cancelled");
      await Promise.all([refresh(), loadJobDetail(jobID, true, ownerBaseURL)]);
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to cancel job");
      else toast.error("Failed to cancel job");
    }
  }

  async function deleteSelectedJob(jobID: string, ownerBaseURL?: string) {
    try {
      const settingsOverride = ownerBaseURL
        ? { ...readUISettings(), hub_base: normalizeBaseURL(ownerBaseURL) }
        : ownerSettings(jobID);
      await apiRequest(`/api/v1/jobs/${encodeURIComponent(jobID)}`, {
        method: "DELETE",
        timeoutMs: 20000,
      }, settingsOverride);
      toast.success("Job deleted");
      setSelectedJobID(null);
      setJobDetail(null);
      setJobDetailError("");
      await refresh();
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to delete job");
      else toast.error("Failed to delete job");
    }
  }

  useEffect(() => {
    if (!selectedJobID || !jobDetail || isTerminalState(jobDetail.state)) return;
    const ownerBaseURL = ownerBaseForJob(selectedJobID);
    const id = window.setInterval(() => {
      void loadJobDetail(selectedJobID, true, ownerBaseURL);
    }, 5000);
    return () => window.clearInterval(id);
  }, [selectedJobID, jobDetail?.state, jobOwnerBaseByID]);

  const stats = useMemo(() => {
    const running   = jobs.filter((j) => j.state === "running").length;
    const queued    = jobs.filter((j) => j.state === "queued").length;
    const completed = jobs.filter((j) => j.state === "completed").length;
    const spendNow  = jobs.filter((j) => !j.history).reduce((acc, j) => acc + j.spent, 0);
    return { running, queued, completed, spendNow };
  }, [jobs]);

  const visible = useMemo(() => {
    const list = jobs
      .filter((j) => (tab === "running" ? !j.history : j.history))
      .sort((a, b) => {
        if (b.updatedAtMs !== a.updatedAtMs) return b.updatedAtMs - a.updatedAtMs;
        return b.id.localeCompare(a.id);
      });
    if (!query) return list;
    const q = query.toLowerCase();
    return list.filter((j) =>
      [j.id, j.client, j.gpu, j.region, j.state].some((v) => v.toLowerCase().includes(q)),
    );
  }, [jobs, tab, query]);

  const tabs: { id: TabId; label: string; count: number; icon: LucideIcon }[] = [
    { id: "running", label: "Running", count: jobs.filter((j) => !j.history).length, icon: Activity },
    { id: "history", label: "History", count: jobs.filter((j) =>  j.history).length, icon: History },
  ];
  const visibleJobCostBands = assignHardwareCostBands(visible, (job) => job.pricePerHr);

  return (
    <>
      <Topbar title="Run Workloads" subtitle="Pick hardware, tune your workload, and run across distributed compute." />
      <main className="flex-1 space-y-6 p-6">
        {/* Hero CTA */}
        <section className="glass relative flex flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl px-6 py-10">
          <div aria-hidden className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full opacity-30 blur-3xl" style={{ background: "var(--gradient-primary)" }} />
          <div aria-hidden className="pointer-events-none absolute -right-24 -bottom-24 h-64 w-64 rounded-full opacity-20 blur-3xl" style={{ background: "var(--gradient-primary)" }} />
          <button
            onClick={() => setWizard(true)}
            className="relative inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-[var(--shadow-glow)]"
            style={{ background: "var(--gradient-primary)" }}
          >
            <Plus className="h-4 w-4" /> New job
          </button>
          <p className="relative text-[11px] text-muted-foreground">Pick a bid, add inputs, and launch jobs with live lifecycle visibility.</p>
        </section>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatChip label="Running"     value={stats.running}            icon={Activity}    hint="Active right now"    tone="mint" />
          <StatChip label="Queued"      value={stats.queued}             icon={Clock}       hint="Awaiting dispatch"   tone="amber" />
          <StatChip label="Completed"   value={stats.completed}          icon={CheckCircle2} hint="Last 24h"           tone="cyan" />
          <StatChip label="Live spend"  value={fmtUsd(stats.spendNow)}   icon={Coins}       hint="Across active jobs"  tone="violet" />
        </div>

        {/* Sub tabs + actions */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 rounded-full border hairline bg-soft p-1 w-fit">
            {tabs.map((t) => {
              const active = tab === t.id;
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition",
                    active ? "text-white shadow-[var(--shadow-glow)]" : "text-muted-foreground hover:text-foreground",
                  )}
                  style={active ? { background: "var(--gradient-primary)" } : undefined}
                >
                  <Icon className="h-3 w-3" />
                  {t.label}
                  <span className={cn(
                    "rounded-full px-1.5 py-0.5 text-[10px]",
                    active ? "bg-white/20" : "border hairline bg-background",
                  )}>{t.count}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search jobs…"
                className="h-8 w-56 rounded-full border hairline bg-soft pl-8 pr-3 text-xs outline-none placeholder:text-muted-foreground focus:border-primary/40"
              />
            </div>
            <button
              onClick={() => void refresh()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border hairline bg-soft px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <RefreshCcw className={cn("h-3.5 w-3.5", busy && "animate-spin")} /> Refresh
            </button>
          </div>
        </div>

        {/* Table */}
        <GlassPanel
          title={tab === "running" ? "Active jobs" : "Job history"}
          subtitle={tab === "running" ? "Live + queued jobs across connected partners · click a row to view status" : "Settled jobs with final spend · click a row to view status"}
        >
          {visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl border hairline bg-soft">
                {tab === "running" ? <Activity className="h-5 w-5 text-muted-foreground" /> : <History className="h-5 w-5 text-muted-foreground" />}
              </div>
              <p className="mt-3 text-sm font-medium">
                {tab === "running" ? "No jobs running right now." : "Nothing in history yet."}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {tab === "running" ? "Launch a new job to see it appear here in real time." : "Completed and failed jobs land here once they settle."}
              </p>
              {tab === "running" && (
                <button
                  onClick={() => setWizard(true)}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)]"
                  style={{ background: "var(--gradient-primary)" }}
                >
                  <Plus className="h-3 w-3" /> New job
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b hairline text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-2 py-2.5 text-left font-medium">Job</th>
                    <th className="px-2 py-2.5 text-left font-medium">State</th>
                    <th className="px-2 py-2.5 text-left font-medium">Client</th>
                    <th className="px-2 py-2.5 text-left font-medium">GPU</th>
                    <th className="px-2 py-2.5 text-left font-medium">Region</th>
                    <th className="px-2 py-2.5 text-right font-medium">Cost band</th>
                    <th className="px-2 py-2.5 text-right font-medium">Spent</th>
                    <th className="px-2 py-2.5 text-right font-medium">Updated</th>
                    <th className="px-2 py-2.5 text-right font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((j, index) => {
                    const p = statePill(j.state);
                    const costBand = hardwareCostBandLabel(
                      visibleJobCostBands[index] || "Mid cost",
                    );
                    return (
                      <tr
                        key={j.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => { void openJobStatus(j.id); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            void openJobStatus(j.id);
                          }
                        }}
                        className="cursor-pointer border-b hairline transition hover:bg-soft focus-visible:bg-soft"
                      >
                        <td className="px-2 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg border hairline bg-soft text-primary">
                              <Cpu className="h-3.5 w-3.5" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-mono text-[12px] font-medium leading-tight">{j.id}</p>
                              <p className="text-[11px] text-muted-foreground">{costBand}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium", p.cls)}>
                            {p.dot && (
                              <span className="relative flex h-1.5 w-1.5">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--success)] opacity-60" />
                                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--success)]" />
                              </span>
                            )}
                            {p.label}
                          </span>
                        </td>
                        <td className="px-2 py-3 text-muted-foreground">{j.client}</td>
                        <td className="px-2 py-3 font-mono text-[12px]">{j.gpu}</td>
                        <td className="px-2 py-3 text-muted-foreground">
                          <span className="inline-flex items-center gap-1"><MapPinIcon className="h-3 w-3" />{j.region}</span>
                        </td>
                        <td className="px-2 py-3 text-right text-[12px] font-medium">{costBand}</td>
                        <td className="px-2 py-3 text-right font-mono text-[12px]">{fmtUsd(j.spent)}</td>
                        <td className="px-2 py-3 text-right text-xs text-muted-foreground">{j.updated}</td>
                        <td className="px-2 py-3 text-right text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5">
                            Status
                            <ChevronRight className="h-3 w-3" />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </GlassPanel>
      </main>

      {wizard && (
        <RunJobWizard
          bids={bids}
          setBids={setBids}
          availableCreditMicros={availableCreditMicros}
          providerBalanceByKey={providerBalanceByKey}
          onClose={() => setWizard(false)}
          onLaunched={async () => {
            setWizard(false);
            await refresh();
          }}
        />
      )}

      {selectedJobID && (
        <JobStatusModal
          jobID={selectedJobID}
          detail={jobDetail}
          busy={jobDetailBusy}
          error={jobDetailError}
          onCancel={() => {
            const ownerBaseURL = ownerBaseForJob(selectedJobID);
            void cancelSelectedJob(selectedJobID, ownerBaseURL);
          }}
          onDelete={() => {
            const ownerBaseURL = ownerBaseForJob(selectedJobID);
            void deleteSelectedJob(selectedJobID, ownerBaseURL);
          }}
          onClose={() => {
            setSelectedJobID(null);
            setJobDetail(null);
            setJobDetailError("");
          }}
          onRefresh={() => {
            const ownerBaseURL = ownerBaseForJob(selectedJobID);
            void loadJobDetail(selectedJobID, false, ownerBaseURL);
          }}
        />
      )}
    </>
  );
}

function JobStatusModal({
  jobID,
  detail,
  busy,
  error,
  onCancel,
  onDelete,
  onClose,
  onRefresh,
}: {
  jobID: string;
  detail: BackendJobDetail | null;
  busy: boolean;
  error: string;
  onCancel: () => void;
  onDelete: () => void;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const rawState = String(detail?.state || "PENDING");
  const state = statePill(mapJobState(rawState));
  const stateLabel = rawState.replaceAll("_", " ");
  const terminal = isTerminalState(rawState);
  const canCancel = Boolean(detail) && !terminal;
  const canDelete = Boolean(detail) && terminal;
  const provider = String(detail?.provisioned_provider || detail?.selected_provider || detail?.request?.selected_provider || "—");
  const region = String(detail?.provisioned_region || detail?.selected_region || detail?.request?.selected_region || "—");
  const gpuType = String(detail?.provisioned_gpu_type || detail?.selected_gpu_type || detail?.request?.selected_gpu_type || "GPU");
  const gpuCount = Number(detail?.provisioned_gpu_count || detail?.selected_gpu_count || detail?.request?.selected_gpu_count || 1);
  const currency = String(detail?.billed_currency || detail?.provisioned_currency || detail?.selected_currency || "USD");
  const billedTotal = Number(detail?.billed_total_value || 0);
  const lifecycle = lifecycleStates(rawState, detail?.progress_stage);
  const activeLifecycleIdx = lifecycleActiveIndex(rawState, detail?.progress_stage);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-md" onClick={onClose} />
      <div className="glass-strong relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl">
        <header className="flex items-start justify-between border-b hairline p-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Job status</p>
            <h2 className="mt-1 font-mono text-sm font-semibold tracking-tight">{jobID}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border hairline bg-soft px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <RefreshCcw className={cn("h-3.5 w-3.5", busy && "animate-spin")} />
              Refresh
            </button>
            <button onClick={onClose} className="rounded-full border hairline bg-soft p-1.5 text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </header>

        <div className="max-h-[62vh] space-y-4 overflow-y-auto p-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium", state.cls)}>
              {state.dot && (
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--success)] opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--success)]" />
                </span>
              )}
              {stateLabel}
            </span>
            {detail?.progress_stage && (
              <span className="rounded-full border hairline bg-soft px-2 py-0.5 text-[11px] text-muted-foreground">
                Stage: {detail.progress_stage}
              </span>
            )}
            {!isTerminalState(rawState) && <span className="text-[11px] text-muted-foreground">Auto-refresh every 5s while open</span>}
          </div>

          <JobLifecycleBar
            states={lifecycle}
            activeIndex={activeLifecycleIdx}
          />

          {error && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {busy && !detail ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <RefreshCcw className="h-4 w-4 animate-spin" />
              Loading live job status…
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <StatusField label="Provider" value={provider} />
                <StatusField label="Region" value={region} />
                <StatusField label="GPU" value={`${gpuType} × ${gpuCount}`} mono />
                <StatusField label="Billed" value={`${fmtUsd(billedTotal)} ${currency}`} mono />
                <StatusField label="Created" value={fmtDateTime(detail?.created_at)} />
                <StatusField label="Updated" value={fmtDateTime(detail?.updated_at)} />
                <StatusField label="VM Ready" value={detail?.vm_ready ? `Yes · ${fmtDateTime(detail?.vm_ready_at)}` : "No"} />
                <StatusField label="VM Terminated" value={fmtDateTime(detail?.vm_terminated_at)} />
              </div>

              <div className="rounded-xl border hairline bg-soft p-3">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Progress</p>
                <p className="mt-1 text-sm font-medium">{detail?.progress_message || "No progress message yet."}</p>
                {detail?.progress_detail && (
                  <p className="mt-1 text-xs text-muted-foreground">{detail.progress_detail}</p>
                )}
              </div>

              {(detail?.failure_code || detail?.failure_detail) && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3">
                  <p className="text-[11px] uppercase tracking-wider text-destructive">Failure</p>
                  <p className="mt-1 text-sm font-medium text-destructive">{detail.failure_code || "FAILED"}</p>
                  {detail.failure_detail && <p className="mt-1 text-xs text-destructive/90">{detail.failure_detail}</p>}
                </div>
              )}

              {detail?.output_plaintext && (
                <div className="rounded-xl border hairline bg-soft p-3">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Output preview</p>
                  <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap text-xs">{detail.output_plaintext}</pre>
                </div>
              )}
            </>
          )}
        </div>

        <footer className="flex items-center justify-between border-t hairline p-5">
          <p className="text-xs text-muted-foreground">
            {canDelete ? "This job is terminal and can be removed from history." : "Cancel stops an in-flight job. Delete is enabled after it reaches a terminal state."}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              disabled={!canCancel || busy}
              className="rounded-full border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive disabled:opacity-40"
            >
              Cancel job
            </button>
            <button
              onClick={onDelete}
              disabled={!canDelete || busy}
              className="rounded-full border hairline bg-soft px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-40"
            >
              Delete job
            </button>
            <button
              onClick={onClose}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)]"
              style={{ background: "var(--gradient-primary)" }}
            >
              Close
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function StatusField({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-xl border hairline bg-soft p-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={cn("mt-1 text-sm", mono && "font-mono text-[12px]")}>{value || "—"}</p>
    </div>
  );
}

function JobLifecycleBar({
  states,
  activeIndex,
}: {
  states: JobLifecycleState[];
  activeIndex: number;
}) {
  return (
    <div className="rounded-xl border hairline bg-soft p-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Lifecycle</p>
      <div className="mt-2 overflow-x-auto">
        <div className="flex min-w-[680px] items-center gap-2">
          {JOB_LIFECYCLE_STEPS.map((step, idx) => {
            const state = states[idx] || "pending";
            const isDone = state === "done";
            const isActive = state === "active";
            const isFailed = state === "failed";
            const connectorOn = isDone || isActive;
            return (
              <div key={step.key} className="flex flex-1 items-center gap-2">
                <div
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold",
                    isDone && "border-[var(--success)] bg-[var(--success)] text-background",
                    isActive && "border-primary text-primary",
                    isFailed && "border-destructive bg-destructive/15 text-destructive",
                    state === "pending" && "hairline text-muted-foreground",
                  )}
                >
                  {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : isFailed ? <AlertTriangle className="h-3.5 w-3.5" /> : idx + 1}
                </div>
                <div className="min-w-0">
                  <p className={cn("text-xs font-medium", isDone || isActive || isFailed ? "text-foreground" : "text-muted-foreground")}>{step.label}</p>
                  <p className="truncate text-[10px] text-muted-foreground">{step.hint}</p>
                </div>
                {idx < JOB_LIFECYCLE_STEPS.length - 1 && (
                  <div className={cn("mx-1 h-px flex-1", connectorOn ? "bg-primary/50" : "bg-[var(--border)]")} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Current: <span className="text-foreground">{JOB_LIFECYCLE_STEPS[Math.max(0, Math.min(activeIndex, JOB_LIFECYCLE_STEPS.length - 1))].label}</span>
      </p>
    </div>
  );
}

/* ---------------- Wizard ---------------- */

type ModelSource = "mine" | "huggingface";
type LoadMode = "preloaded" | "hot";

function RunJobWizard({
  bids,
  setBids,
  availableCreditMicros,
  providerBalanceByKey,
  onClose,
  onLaunched,
}: {
  bids: LiveBid[];
  setBids: Dispatch<SetStateAction<LiveBid[]>>;
  availableCreditMicros: number;
  providerBalanceByKey: Record<string, { creditMicros: number; thresholdMicros: number }>;
  onClose: () => void;
  onLaunched: () => Promise<void>;
}) {
  const [step, setStep] = useState(1);
  const [bidIdx, setBidIdx] = useState<number | null>(null);
  const [filterRegion, setFilterRegion] = useState("All");
  const [filterGpu, setFilterGpu] = useState("All");
  const [filterCost, setFilterCost] = useState("All");
  const [modelSource, setModelSource] = useState<ModelSource>("mine");
  const [loadMode, setLoadMode] = useState<LoadMode>("preloaded");
  const [runtimeModelRef, setRuntimeModelRef] = useState("deepseek-ai/DeepSeek-R1-Distill-Qwen-7B");
  const [weightsRef, setWeightsRef] = useState("");
  const [hfModel, setHFModel] = useState("deepseek-ai/DeepSeek-R1-Distill-Qwen-7B");
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(1);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(0.95);
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [runtimeSpokeHealthy, setRuntimeSpokeHealthy] = useState<boolean | null>(null);
  const [runtimeHubID, setRuntimeHubID] = useState("");

  const steps = ["Pick bid", "Model", "Inputs"] as const;

  const finBids = useMemo(() => bids.map(toFinancialBid), [bids]);
  const regions = ["All", ...Array.from(new Set(bids.map((b) => b.location)))];
  const gpuTypes = ["All", ...Array.from(new Set(bids.map((b) => b.gpu)))];
  const [filterRoute, setFilterRoute] = useState<RouteType | "All">("All");

  const eligibleBids = bids
    .map((b, i) => ({ ...b, _i: i }))
    .filter((b) => filterRegion === "All" || b.location === filterRegion)
    .filter((b) => filterGpu === "All" || b.gpu === filterGpu)
    .filter((b) => filterRoute === "All" || finBids[b._i].route_type === filterRoute)
    .sort((left, right) => left.price - right.price || left.gpu.localeCompare(right.gpu));
  const eligibleCostBands = assignHardwareCostBands(eligibleBids, (bid) => bid.price);
  const filteredBids = eligibleBids
    .map((bid, index) => ({
      ...bid,
      _costBand: hardwareCostBandLabel(eligibleCostBands[index] || "Mid cost"),
    }))
    .filter((bid) => filterCost === "All" || bid._costBand === filterCost);

  const selectedBid = bidIdx !== null ? bids[bidIdx] : null;
  const selectedFin = bidIdx !== null ? finBids[bidIdx] : null;
  const selectedCostBand = eligibleBids.findIndex((bid) => bid._i === bidIdx);
  const selectedCostBandLabel = hardwareCostBandLabel(
    eligibleCostBands[selectedCostBand] || "Mid cost",
  );
  const selectedSpokeID = String(selectedBid?.spokeId || "").trim();
  const activeQuote = selectedFin ? buildQuote(selectedFin, duration) : null;
  const selectedSourceSlug = slugToken(String(selectedBid?.sourceSlug || selectedFin?.provider_source_id || ""));
  const selectedProviderSlug = slugToken(String(selectedBid?.providerSlug || ""));
  const providerBalance = selectedSourceSlug
    ? providerBalanceByKey[`source:${selectedSourceSlug}`]
    : selectedProviderSlug
      ? providerBalanceByKey[`provider:${selectedProviderSlug}`]
      : undefined;
  const providerBalanceMicros = String(providerBalance?.creditMicros ?? 0);
  const providerThresholdMicros = String(providerBalance?.thresholdMicros ?? 0);

  const readiness = selectedFin && activeQuote
    ? computeReadiness({
        routeType: selectedFin.route_type,
        availableCreditMicros: String(availableCreditMicros),
        requiredReserveMicros: activeQuote.reserve_amount_micros,
        providerBalanceMicros,
        providerThresholdMicros,
      })
    : "BLOCKED";
  const creditReady = readiness === "READY_INTERNAL" || readiness === "READY_PEER";
  const selectedSpokeMismatch = selectedFin?.route_type === "INTERNAL"
    && runtimeHubID.length > 0
    && selectedSpokeID.length > 0
    && runtimeHubID.toLowerCase() !== selectedSpokeID.toLowerCase();
  const internalSpokeBlocked = selectedFin?.route_type === "INTERNAL"
    && (runtimeSpokeHealthy === false || selectedSpokeMismatch);
  const ready = creditReady && !internalSpokeBlocked;

  const canContinue =
    (step === 1 && selectedBid !== null) ||
    (step === 2) ||
    (step === 3 && ready);

  useEffect(() => {
    let cancelled = false;
    const refreshRuntimeHealth = async () => {
      try {
        const health = await apiRequest<RuntimeHealthPayload>("/api/v1/runtime/health", {
          method: "GET",
          timeoutMs: 7000,
        });
        if (cancelled) return;
        setRuntimeSpokeHealthy(Boolean(health?.spoke));
        setRuntimeHubID(String(health?.hub_id || "").trim());
      } catch {
        if (cancelled) return;
        setRuntimeSpokeHealthy(null);
        setRuntimeHubID("");
      }
    };
    void refreshRuntimeHealth();
    const id = window.setInterval(() => {
      void refreshRuntimeHealth();
    }, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  async function loadFreshSelectedBid(currentBid: LiveBid): Promise<LiveBid> {
    const offersPayload = await apiRequest<{ offers?: BackendMarketOffer[] }>("/api/v1/market/offers", {
      method: "GET",
      timeoutMs: 20000,
    });
    const liveOffers = Array.isArray(offersPayload.offers) ? offersPayload.offers : [];
    const liveBids = liveOffers.map(mapBackendOffer).filter((bid) => bid.capacity > 0 && bid.price > 0);
    const refreshed = liveBids.find((bid) => sameLiveBidIdentity(bid, currentBid));
    if (!refreshed) {
      throw new APIError(
        409,
        "ERR_SELECTED_BID_UNAVAILABLE",
        "Selected bid is no longer live. Refresh bids and pick a current offer.",
      );
    }
    return refreshed;
  }

  async function launchSelectedJob() {
    if (bidIdx === null || !selectedBid || !selectedFin) return;
    if (selectedFin.route_type !== "INTERNAL" && availableCreditMicros <= 0) {
      toast.error("Purchase credits before running a task.");
      return;
    }
    setSubmitting(true);
    try {
      if (selectedFin.route_type === "INTERNAL") {
        const health = await apiRequest<RuntimeHealthPayload>("/api/v1/runtime/health", {
          method: "GET",
          timeoutMs: 7000,
        });
        const localSpokeActive = Boolean(health?.spoke);
        const localHubID = String(health?.hub_id || "").trim();
        if (!localSpokeActive) {
          throw new APIError(
            409,
            "ERR_SPOKE_NOT_ACTIVE",
            "Your local spoke is offline. Go to System and turn on Spoke, then retry.",
          );
        }
        if (
          localHubID.length > 0
          && selectedSpokeID.length > 0
          && localHubID.toLowerCase() !== selectedSpokeID.toLowerCase()
        ) {
          throw new APIError(
            409,
            "ERR_SPOKE_NOT_ACTIVE",
            "Selected internal bid belongs to a different hub session. Refresh bids and pick a current internal offer.",
          );
        }
      }

      const now = new Date();
      const settlementPeriod = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
      const financeUser = financeUserID("");
      const selectedSource = slugToken(String(selectedBid.sourceSlug || ""));
      const selectedProvider = slugToken(String(selectedBid.providerSlug || ""));
      const selectedRouteID = String(selectedBid.routeID || "").trim();
      const selectedRegion = String(selectedBid.location || "").trim();
      const selectedGPU = String(selectedBid.gpu || "").trim();
      const selectedGPUType = String(selectedBid.gpuType || selectedGPU || "").trim();
      const selectedFlavorName = String(selectedBid.flavorName || "").trim();
      const buildPrepareBody = (launchBid: LiveBid): Record<string, unknown> => {
        const launchSource = slugToken(String(launchBid.sourceSlug || ""));
        const launchProvider = slugToken(String(launchBid.providerSlug || ""));
        const launchRouteID = String(launchBid.routeID || "").trim();
        const launchRegion = String(launchBid.location || "").trim();
        const launchGPU = String(launchBid.gpu || "").trim();
        const launchGPUType = String(launchBid.gpuType || launchGPU || "").trim();
        const launchFlavorName = String(launchBid.flavorName || "").trim();
        return {
          client_id: financeUser || undefined,
          prompt: prompt.trim() || "Run this workload",
          model_weights: modelSource === "huggingface" ? hfModel.trim() : runtimeModelRef.trim(),
          model_weights_ref: weightsRef.trim() || undefined,
          runtime_load_mode: loadMode === "hot" ? "hot_load" : "preloaded",
          runtime_model_ref: modelSource === "huggingface" ? hfModel.trim() : runtimeModelRef.trim(),
          generation_max_tokens: maxTokens,
          generation_temperature: temperature,
          generation_top_p: topP,
          selected_provider: launchBid.source,
          selected_provider_slug: launchProvider || undefined,
          selected_source_slug: launchSource || undefined,
          selected_provider_owner_user_id: launchBid.providerOwnerUserID || undefined,
          selected_route_id: launchRouteID || undefined,
          selected_flavor_name: launchFlavorName || undefined,
          selected_region: launchRegion || undefined,
          selected_gpu_type: launchGPUType || undefined,
          selected_gpu_count: launchBid.count || 1,
          selected_price_gpu_hr_value: launchBid.price,
          selected_currency: "USD",
          selected_option_id: launchBid.optionId || undefined,
          selected_spoke_id: launchBid.spokeId || undefined,
          selected_offer_token: launchBid.offerToken || undefined,
          gpu_type: launchGPUType || undefined,
          gpu_count: launchBid.count || 1,
          estimated_duration_hours: duration,
          required_start_by_utc: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
          data_tier: "STANDARD",
          cc_required: false,
          required_regions: launchRegion ? [launchRegion] : undefined,
          compliance_tier: "standard",
          max_price_gpu_hr_value: launchBid.price,
          local_only: false,
          min_external_gpu_count: launchBid.count || 1,
          settlement_period_id: settlementPeriod,
        };
      };

      let launchBid = selectedBid;
      let prepared: any;
      try {
        prepared = await apiRequest<any>("/api/v1/jobs/prepare", {
          method: "POST",
          body: buildPrepareBody(launchBid),
          timeoutMs: 45000,
        });
      } catch (err) {
        if (!(err instanceof APIError) || err.code !== "ERR_SELECTED_OFFER_TOKEN_EXPIRED") {
          throw err;
        }
        launchBid = await loadFreshSelectedBid(selectedBid);
        setBids((current) => current.map((bid, idx) => (idx === bidIdx ? launchBid : bid)));
        prepared = await apiRequest<any>("/api/v1/jobs/prepare", {
          method: "POST",
          body: buildPrepareBody(launchBid),
          timeoutMs: 45000,
        });
      }

      const preparedJob = prepared?.job ?? prepared;
      const jobID = String(preparedJob?.request?.job_id || preparedJob?.job_id || "").trim();
      if (!jobID) {
        throw new APIError(500, "ERR_JOB_PREPARE_INVALID", "Job prepare response missing job id");
      }

      const acceptedBy = financeUserID("");
      await apiRequest(`/api/v1/jobs/${encodeURIComponent(jobID)}/confirm`, {
        method: "POST",
        body: {
          accepted_by: acceptedBy || undefined,
          option_id: launchBid.optionId || undefined,
          spoke_id: launchBid.spokeId || undefined,
          expected_price_gpu_hr: launchBid.price,
          expected_currency: "USD",
        },
        timeoutMs: 45000,
      });

      toast.success(`Job ${jobID} launched`);
      await onLaunched();
    } catch (err) {
      if (err instanceof APIError) {
        if (
          err.code === "ERR_SPOKE_NOT_ACTIVE"
          || /ERR_SPOKE_NOT_ACTIVE|selected spoke .* not active/i.test(String(err.detail || ""))
        ) {
          toast.error("Spoke is offline for this internal offer. Turn on Spoke in System, refresh bids, then run again.");
        } else if (
          err.code === "ERR_SELECTED_OFFER_TOKEN_EXPIRED"
          || err.code === "ERR_SELECTED_BID_UNAVAILABLE"
        ) {
          toast.error("Selected bid expired or disappeared. Refresh bids and pick a current offer.");
        } else {
          toast.error(err.detail || "Failed to launch job");
        }
      } else {
        toast.error("Failed to launch job");
      }
    } finally {
      setSubmitting(false);
      setConfirm(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-md" onClick={onClose} />
      <div className="glass-strong relative z-10 w-full max-w-4xl overflow-hidden rounded-2xl">
        <header className="flex items-start justify-between border-b hairline p-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">New job</p>
            <h2 className="mt-1 font-display text-lg font-semibold tracking-tight">{steps[step - 1]}</h2>
          </div>
          <button onClick={onClose} className="rounded-full border hairline bg-soft p-1.5 text-muted-foreground hover:text-foreground">
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        {/* Stepper */}
        <div className="flex items-center gap-2 border-b hairline px-5 py-3">
          {steps.map((s, i) => {
            const n = i + 1;
            const active = n === step;
            const done = n < step;
            return (
              <div key={s} className="flex flex-1 items-center gap-2">
                <span className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold transition",
                  done ? "border-transparent text-white" : active ? "border-primary text-primary" : "hairline text-muted-foreground",
                )} style={done ? { background: "var(--gradient-primary)" } : undefined}>
                  {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
                </span>
                <span className={cn("text-xs font-medium", active ? "text-foreground" : "text-muted-foreground")}>{s}</span>
                {i < steps.length - 1 && <div className="ml-1 h-px flex-1 bg-[var(--border)]" />}
              </div>
            );
          })}
        </div>

        {/* Body */}
        <div className="max-h-[62vh] overflow-y-auto p-5">
          {/* STEP 1: bid */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2 rounded-xl border hairline bg-soft p-3">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                <FilterSelect label="Region" value={filterRegion} onChange={setFilterRegion} options={regions} />
                <FilterSelect label="GPU" value={filterGpu} onChange={setFilterGpu} options={gpuTypes} />
                <FilterSelect label="Route" value={filterRoute} onChange={(v) => setFilterRoute(v as RouteType | "All")} options={["All", "INTERNAL", "PEER", "GENRL_NATIVE"]} />
                <FilterSelect
                  label="Cost"
                  value={filterCost}
                  onChange={setFilterCost}
                  options={["All", ...HARDWARE_COST_BANDS.map(hardwareCostBandLabel)]}
                />
                <span className="ml-auto text-[11px] text-muted-foreground">{filteredBids.length} live offers</span>
              </div>

              {/* Bid grid */}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {filteredBids.map((b) => {
                  const sel = bidIdx === b._i;
                  const fin = finBids[b._i];
                  return (
                    <button
                      key={b._i}
                      onClick={() => setBidIdx(b._i)}
                      className={cn(
                        "group relative overflow-hidden rounded-xl border bg-soft p-4 text-left transition",
                        sel ? "border-primary shadow-[var(--shadow-glow)]" : "hairline hover:border-primary/40",
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <RouteBadge routeType={fin.route_type} />
                            <span className="text-[11px] text-muted-foreground">{b.source}</span>
                          </div>
                          <p className="mt-2 text-sm font-semibold leading-tight">{b.gpu}</p>
                          <p className="text-[11px] text-muted-foreground">{b.location} · {b.capacity} available</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold">{b._costBand}</p>
                          <p className="text-[10px] text-muted-foreground">relative live cost</p>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                        <div>
                          <p className="text-muted-foreground">Billing route</p>
                          <p className="font-medium">{fin.route_type === "INTERNAL" ? "Connected provider" : "GENRL credits"}</p>
                        </div>
                        <div><p className="text-muted-foreground">Stock</p><p className="font-medium">{b.capacity}</p></div>
                      </div>
                    </button>
                  );
                })}
                {filteredBids.length === 0 && (
                  <div className="md:col-span-2 rounded-xl border hairline bg-soft p-8 text-center">
                    <p className="text-sm font-medium">No matching bids</p>
                    <p className="mt-1 text-xs text-muted-foreground">Loosen filters to see more live offers.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 2: model */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {([
                  { id: "mine",        title: "My model",     desc: "Use your own runtime + weights from storage.", icon: Boxes },
                  { id: "huggingface", title: "Hugging Face", desc: "Pull a public or licensed model directly.",    icon: Sparkles },
                ] as const).map((opt) => {
                  const sel = modelSource === opt.id;
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setModelSource(opt.id)}
                      className={cn(
                        "rounded-xl border p-4 text-left transition",
                        sel ? "border-primary bg-soft shadow-[var(--shadow-glow)]" : "hairline bg-soft hover:border-primary/40",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-primary" />
                        <p className="text-sm font-semibold">{opt.title}</p>
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground">{opt.desc}</p>
                    </button>
                  );
                })}
              </div>

              {modelSource === "mine" ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="Runtime model ref" value={runtimeModelRef} onChange={setRuntimeModelRef} placeholder="e.g. deepseek-ai/DeepSeek-R1-Distill-Qwen-7B" />
                  <Field label="Weights ref" value={weightsRef} onChange={setWeightsRef} placeholder="data/upload/abc123 (optional)" />
                  <div className="md:col-span-2 rounded-xl border hairline border-dashed bg-soft p-5 text-center">
                    <Upload className="mx-auto h-5 w-5 text-muted-foreground" />
                    <p className="mt-2 text-xs font-medium">Drop weights file (optional)</p>
                    <p className="text-[11px] text-muted-foreground">.safetensors · .bin · .gguf · up to 80 GB</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <Field label="Search Hugging Face" value={hfModel} onChange={setHFModel} placeholder="deepseek-ai/DeepSeek-R1-Distill-Qwen-7B" icon={Search} />
                  <div className="rounded-xl border hairline bg-soft p-4">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Selected</p>
                    <p className="mt-1 font-mono text-sm">{hfModel || "deepseek-ai/DeepSeek-R1-Distill-Qwen-7B"}</p>
                    <p className="text-[11px] text-muted-foreground">Text generation · 8.03B params</p>
                  </div>
                </div>
              )}

              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Runtime load mode</p>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { id: "preloaded", label: "Preloaded", hint: "Start with weights pre-staged on the host" },
                    { id: "hot",       label: "Hot load",  hint: "Stream weights at run time" },
                  ] as const).map((m) => {
                    const sel = loadMode === m.id;
                    return (
                      <button
                        key={m.id}
                        onClick={() => setLoadMode(m.id)}
                        className={cn(
                          "rounded-xl border p-3 text-left transition",
                          sel ? "border-primary bg-soft shadow-[var(--shadow-glow)]" : "hairline bg-soft hover:border-primary/40",
                        )}
                      >
                        <p className="text-sm font-medium">{m.label}</p>
                        <p className="text-[11px] text-muted-foreground">{m.hint}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: inputs + credits */}
          {step === 3 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
              <div className="space-y-3 md:col-span-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Data tier" placeholder="Standard" />
                  <Field label="Estimated duration (h)" type="number" value={String(duration)} onChange={(v) => setDuration(Number(v) || 0)} />
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Prompt</label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={5}
                    placeholder="Describe what to generate, or attach a file."
                    className="w-full resize-none rounded-xl border hairline bg-background p-3 text-sm outline-none focus:border-primary/40"
                  />
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <FileText className="h-3 w-3" />
                    <button className="hover:text-foreground">Attach input file</button>
                  </div>
                </div>

                <div className="rounded-xl border hairline bg-soft p-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Generation</p>
                  <div className="grid grid-cols-3 gap-3">
                    <Slim label="Max tokens"  value={maxTokens}    onChange={setMaxTokens}    step={64}   min={1}  max={32000} />
                    <Slim label="Temperature" value={temperature}  onChange={setTemperature}  step={0.05} min={0}  max={2}     decimals={2} />
                    <Slim label="Top P"       value={topP}         onChange={setTopP}         step={0.01} min={0}  max={1}     decimals={2} />
                  </div>
                </div>
              </div>

              {/* Credits */}
              <div className="md:col-span-2">
                <div className="glass space-y-3 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-primary" />
                    <p className="text-sm font-semibold">Credit & route summary</p>
                    {selectedFin && <RouteBadge routeType={selectedFin.route_type} className="ml-auto" />}
                  </div>
                  {selectedFin && activeQuote ? (
                    <>
                      <Row label="Compute"  value={`${selectedFin.hardware_type} · ${selectedFin.region}`} mono />
                      <Row label="Duration" value={`${duration} GPU·h`} mono />
                      <Row label="Cost band" value={selectedCostBandLabel} />
                      <div className="my-2 h-px bg-[var(--border)]" />
                      {selectedFin.route_type === "INTERNAL" ? (
                        <p className="text-[11px] text-muted-foreground">
                          This job will run on your own connected provider. <span className="text-foreground">GENRL credits will not be debited.</span>
                        </p>
                      ) : (
                        <p className="text-[11px] text-muted-foreground">
                          GENRL will reserve enough credits for the selected route and settle verified usage after the run.
                        </p>
                      )}
                      <div className={cn(
                        "rounded-xl border p-3 text-sm font-medium",
                        ready
                          ? "border-[var(--success)]/30 bg-[var(--success)]/10 text-[var(--success)]"
                          : "border-destructive/30 bg-destructive/10 text-destructive",
                      )}>
                        {readiness === "READY_INTERNAL"
                          ? "Ready to run on your connected provider"
                          : readiness === "READY_PEER"
                            ? "Ready to run"
                            : readiness === "PROVIDER_LOW_BALANCE"
                              ? "Provider balance is below its threshold"
                              : "Add credits to run"}
                      </div>
                      {readiness === "INSUFFICIENT_CREDITS" && availableCreditMicros <= 0 ? (
                        <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-2.5 text-[11px] text-destructive">
                          <span>Purchase credits.</span>
                          <button
                            type="button"
                            onClick={() => window.location.assign("/settings?section=billing")}
                            className="rounded-full bg-destructive px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-60">
                            Manage credits in Settings
                          </button>
                        </div>
                      ) : null}
                      {selectedFin.route_type === "INTERNAL" && runtimeSpokeHealthy === false && (
                        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-2.5 text-[11px] text-destructive">
                          Spoke is offline. Open System and turn on Spoke before running this internal job.
                        </div>
                      )}
                      {selectedFin.route_type === "INTERNAL" && selectedSpokeMismatch && (
                        <div className="rounded-lg border border-[var(--warning)]/30 bg-[var(--warning)]/10 p-2.5 text-[11px] text-[var(--warning)]">
                          This internal offer belongs to a different hub session. Refresh bids and reselect.
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">Select a bid in step 1 to see its route, cost band, and readiness.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-between border-t hairline p-4">
          <div className="text-[11px] text-muted-foreground">
            {step === 1 && (selectedBid
              ? <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-[var(--success)]" /> {selectedBid.gpu} · {selectedCostBandLabel} selected</span>
              : "Pick a bid to continue")}
            {step === 2 && <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3 w-3 text-primary" /> Model configured · {loadMode === "preloaded" ? "preloaded" : "hot load"}</span>}
            {step === 3 && (ready
              ? <span className="inline-flex items-center gap-1.5"><Zap className="h-3 w-3 text-[var(--success)]" /> All checks passed</span>
              : <span className="inline-flex items-center gap-1.5"><AlertTriangle className="h-3 w-3 text-[var(--warning)]" /> Resolve issues to launch</span>)}
          </div>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-1 rounded-full border hairline bg-soft px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3 w-3" /> Back
              </button>
            )}
            {step < 3 ? (
              <button
                disabled={!canContinue}
                onClick={() => setStep((s) => s + 1)}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)] disabled:opacity-40"
                style={{ background: "var(--gradient-primary)" }}
              >
                Continue <ArrowRight className="h-3 w-3" />
              </button>
            ) : (
              <button
                disabled={!ready}
                onClick={() => setConfirm(true)}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)] disabled:opacity-40"
                style={{ background: "var(--gradient-primary)" }}
              >
                <Play className="h-3 w-3" /> Run job
              </button>
            )}
          </div>
        </footer>

        {confirm && selectedBid && selectedFin && (
          <ConfirmModal
            bid={selectedBid}
            fin={selectedFin}
            duration={duration}
            costBand={selectedCostBandLabel}
            submitting={submitting}
            onCancel={() => {
              if (!submitting) setConfirm(false);
            }}
            onConfirm={() => {
              void launchSelectedJob();
            }}
          />
        )}
      </div>
    </div>
  );
}

/* ---------------- Confirm modal ---------------- */

function ConfirmModal({
  bid, fin, duration, costBand, submitting, onCancel, onConfirm,
}: {
  bid: LiveBid;
  fin: ComputedFinancialBid;
  duration: number;
  costBand: string;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const quote = buildQuote(fin, duration);
  const peer = fin.route_type !== "INTERNAL";
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-md" onClick={onCancel} />
      <div className="glass-strong relative z-10 w-full max-w-md rounded-2xl p-5">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl border hairline bg-soft text-primary">
            <Play className="h-4 w-4" />
          </div>
          <div>
            <p className="font-display text-base font-semibold tracking-tight">Confirm & run</p>
            <p className="text-[11px] text-muted-foreground">Quote locked · {ROUTE_LABEL[fin.route_type]} · expires {quote.valid_until}</p>
          </div>
          <RouteBadge routeType={fin.route_type} className="ml-auto" />
        </div>

        <div className="mt-4 space-y-2 rounded-xl border hairline bg-soft p-3 text-sm">
          <Row label="Provider" value={bid.source} />
          <Row label="Flavor"   value={bid.gpu} mono />
          <Row label="Region"   value={bid.location} />
          <Row label="Duration" value={`${duration}h`} mono />
          <Row label="Cost band" value={costBand} />
          <div className="my-1 h-px bg-[var(--border)]" />
          {peer ? (
            <p className="text-[11px] text-muted-foreground">
              GENRL credits will be reserved now and settled after usage is metered.
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              This job uses your own connected provider, so GENRL credits will not be debited.
            </p>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="rounded-full border hairline bg-soft px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)] disabled:opacity-60"
            style={{ background: "var(--gradient-primary)" }}
          >
            <ChevronRight className="h-3 w-3" /> {submitting ? "Launching…" : "Confirm & launch"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- tiny primitives ---------------- */

function FilterSelect({
  label, value, onChange, options,
}: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <label className="inline-flex items-center gap-1.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 rounded-md border hairline bg-background px-2 text-xs outline-none focus:border-primary/40"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Field({
  label, placeholder, value, onChange, type = "text", icon: Icon,
}: { label: string; placeholder?: string; value?: string; onChange?: (v: string) => void; type?: string; icon?: LucideIcon }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="relative">
        {Icon && <Icon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          className={cn(
            "h-9 w-full rounded-md border hairline bg-background px-3 text-sm outline-none focus:border-primary/40",
            Icon && "pl-8",
          )}
        />
      </div>
    </label>
  );
}

function Slim({
  label, value, onChange, step = 1, min = 0, max = 100, decimals = 0,
}: { label: string; value: number; onChange: (v: number) => void; step?: number; min?: number; max?: number; decimals?: number }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{label}</span>
        <span className="font-mono text-foreground">{value.toFixed(decimals)}</span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 w-full accent-[var(--primary)]"
      />
    </div>
  );
}

function Row({
  label, value, mono, bold, muted,
}: { label: string; value: string; mono?: boolean; bold?: boolean; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn(
        mono && "font-mono",
        bold && "font-semibold text-foreground",
        muted && "text-muted-foreground",
      )}>{value}</span>
    </div>
  );
}
